import {isFeatureEnabled} from '@github-ui/feature-flags'
import {reportTraceData} from '@github-ui/internal-api-insights'
import {isStaff} from '@github-ui/stats'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import type {Sink} from 'relay-runtime/lib/network/RelayObservable'
import {CircuitBreaker, type ErrorType} from './circuit-breaker'
import type {Storage} from './types'

import {HOT_CACHE_HIT_EVENT, InMemoryLRUQueryCache} from './in-memory-lru-query-cache'
import {CACHE_EVENTS} from '@github-ui/safe-indexed-db-storage'
import {ensureHPCListenerRegistered, hpcState, tryReportPendingHPCEvent} from './hpc-metrics'

export const INTERNAL_ONLY_ATTRIBUTES = {
  EXCLUDE_FROM_CACHE_KEY: 'exclude_from_cache_key' as const,
}

const PREHEAT_NOOP_EVENT = 'offline_cache.preheat_noop'

interface StoragePersisterOptions {
  /** The storage client used for setting and retrieving items from cache.
   * For SSR pass in `undefined`.
   */
  storage: Storage | undefined | null
  /**
   * The max-allowed age of the cache in milliseconds.
   * If a persisted cache is found that is older than this
   * time, it will be discarded
   */
  maxAgeMs: number
  /**
   * Prefix to be used for storage key.
   * Storage key is a combination of prefix and query hash in a form of `prefix-queryHash`.
   */
  prefix: string
  /**
   * Function to send custom analytics events (that aren't handled by the storage client)
   */
  sendAnalyticsEvent: SendAnalyticsEventFunction
}
type CacheKey = string

declare const InvalidBrand: unique symbol
type Invalid = {
  readonly [InvalidBrand]: true
}
export const INVALID: Invalid = {} as Invalid

/**
 * Generates a cache key from query name and variables
 */
export function generateCacheKey(
  prefix: string,
  queryName: string,
  variables: Record<string, unknown>,
  defaultValues?: Record<string, unknown>,
  excludeFromCacheKey?: string[],
): CacheKey {
  const variablesWithDefaults = {
    ...defaultValues,
    ...Object.fromEntries(Object.entries(variables).filter(([, v]) => v !== undefined)),
  }
  const sortedVariableKeys = Object.keys(variablesWithDefaults).sort()
  const sortedVariables: Record<string, unknown> = {}
  for (const key of sortedVariableKeys) {
    if (excludeFromCacheKey && excludeFromCacheKey.includes(key)) {
      continue
    }
    const value = variablesWithDefaults[key]
    if (value !== undefined && value !== null) {
      sortedVariables[key] = value
    }
  }
  return `${prefix}-${queryName}#${JSON.stringify(sortedVariables)}`
}

export type QueryCache<T> = ReturnType<typeof createQueryCache<T>>

export function retrieveQueryFromHotCache<T>(
  prefix: string,
  queryId: string,
  queryName: string,
  variables: Record<string, unknown>,
  defaultValues?: Record<string, unknown>,
  excludeFromCacheKey?: string[],
): T | undefined | Invalid {
  const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues, excludeFromCacheKey)
  const hotCachedQuery = InMemoryLRUQueryCache.getItem<{queryHash: string; state: {data: T}}>(cacheKey)
  if (hotCachedQuery) {
    if (hotCachedQuery.queryHash !== queryId) {
      return INVALID
    }
    tryReportPendingHPCEvent(queryName, 'hit-hot')
    InMemoryLRUQueryCache.sendAnalyticsEvent(HOT_CACHE_HIT_EVENT, cacheKey, {})
    return hotCachedQuery.state.data
  }
  return undefined
}

export function createQueryCache<T>({storage, prefix, maxAgeMs, sendAnalyticsEvent}: StoragePersisterOptions) {
  const isClientSideCachingAnalyticsEnabled = isFeatureEnabled('issues_react_extended_preheat_analytics')
  const isCircuitBreakerEnabled = isFeatureEnabled('issues_react_client_side_caching_cb')
  const isCustomHPCMetricEnabled = isFeatureEnabled('issues_react_custom_hpc_metric')
  const circuitBreakerKey = `${prefix}-circuit-breaker`
  const circuitBreaker = isCircuitBreakerEnabled ? new CircuitBreaker(circuitBreakerKey, sendAnalyticsEvent) : undefined

  if (isCustomHPCMetricEnabled) {
    // Register the HPC event listener (singleton - only registers once)
    ensureHPCListenerRegistered()
    hpcState.enabled = true
  }

  // Initialize hot cache with analytics for error reporting
  InMemoryLRUQueryCache.init(sendAnalyticsEvent)

  async function retrieveQuery(
    queryId: string,
    queryName: string,
    variables: Record<string, unknown>,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
  ): Promise<T | undefined | Invalid> {
    if (!storage) {
      return undefined
    }
    const keysToExclude = attributes?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY] as string[] | undefined
    const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues, keysToExclude)
    try {
      const persistedQuery = await storage.getItem(cacheKey, removeInternalOnlyAttributes(attributes))
      if (persistedQuery && persistedQuery.queryHash !== queryId) {
        // The persisted query has changed and so the cached version is no longer valid
        return INVALID
      }
      return persistedQuery?.state.data as T | undefined
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_READ_ERROR, cacheKey, {error: String(error)})
      return undefined
    }
  }

  async function evictQuery(cacheKey: CacheKey) {
    if (!storage) {
      return
    }
    try {
      InMemoryLRUQueryCache.removeItem(cacheKey)
      await storage.removeItem(cacheKey)
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT_ERROR, cacheKey, {error: String(error)})
    }
  }

  async function tryCacheFetchResult(
    queryId: string,
    queryName: string,
    variables: Record<string, unknown>,
    fetchResult: T | null,
    ttl: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
    preheatSource?: string,
  ) {
    if (!storage) {
      return undefined
    }
    const keysToExclude = attributes?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY] as string[] | undefined
    const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues, keysToExclude)
    const cacheEntry = {
      queryHash: queryId,
      queryKey: Object.entries(variables).flat(),
      state: {data: fetchResult, dataUpdatedAt: Date.now(), ttl, preheatSource},
    }
    try {
      // only write to hot cache if preheating
      if (preheatSource) {
        InMemoryLRUQueryCache.setItem(cacheKey, cacheEntry)
      }
      await storage.setItem(cacheKey, cacheEntry, undefined, undefined, removeInternalOnlyAttributes(attributes))
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_WRITE_ERROR, cacheKey, {error: String(error)})
    }
  }

  async function persisterFn(
    queryFn: (isCacheRefresh?: boolean) => Promise<T>,
    queryId: string,
    queryName: string,
    variables: Record<string, unknown>,
    observer: Sink<T>,
    ttl?: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
    preloadSubscriptions?: (resolved: T | undefined) => void,
  ): Promise<T | undefined> {
    const cachedResult = await retrieveQuery(queryId, queryName, variables, attributes, defaultValues)
    const keysToExclude = attributes?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY] as string[] | undefined
    const cacheKey = generateCacheKey(prefix, queryName, variables, defaultValues, keysToExclude)
    const hasCachedResult = cachedResult !== undefined && cachedResult !== INVALID
    const preheatSource = attributes?.preheat_source as string | undefined
    const preHeated = !!preheatSource
    const skipCache = !!(attributes?.skip_cache as boolean | undefined)

    let cachedResultString: string | null = null
    let queryResultString: string | null = null
    if (hasCachedResult) {
      if (typeof cachedResult === 'object' && cachedResult !== null && 'data' in cachedResult) {
        cachedResultString = JSON.stringify((cachedResult as {[key: string]: unknown}).data)
      }
      reportTraceData({
        __trace: {
          query_name: attributes?.query_name as string | undefined,
          query_id: queryId,
          query_variables: variables,
          duration_ms: 1,
          cache_result: 'hit',
        },
      })
      if (!preHeated && !skipCache) {
        tryReportPendingHPCEvent(queryName, 'hit')
        preloadSubscriptions?.(cachedResult as T)
        observer.next(cachedResult as T)
        // Dispatch cache hit event for staff bar indicator
        dispatchCacheHitEvent()
      }
    } else {
      tryReportPendingHPCEvent(queryName, 'miss')
    }

    if (preHeated && hasCachedResult) {
      const cacheEntry = {
        queryHash: queryId,
        queryKey: Object.entries(variables).flat(),
        state: {data: cachedResult, dataUpdatedAt: Date.now(), ttl, preheatSource},
      }
      InMemoryLRUQueryCache.setItem(cacheKey, cacheEntry)
      // If preHeated is set and we have a cached result, don't return anything
      if (isClientSideCachingAnalyticsEnabled && preheatSource) {
        sendAnalyticsEvent(PREHEAT_NOOP_EVENT, cacheKey, {preheatSource})
      }
      return
    }

    const circuitBreakerStatus = circuitBreaker?.getStatus() ?? 'CLOSED'
    let fetchResult: T
    try {
      if (preHeated) {
        if (circuitBreakerStatus === 'OPEN') {
          return
        }
      }
      fetchResult = await queryFn(hasCachedResult)
    } catch (error) {
      // If queryFn throws and we have cached data, emit stale cache warning
      // and return the cached result to prevent UI breakage
      if (hasCachedResult) {
        dispatchStaleCacheWarning([{message: error instanceof Error ? error.message : 'Unknown error'}])
        return cachedResult as T
      }
      // If no cached data, re-throw the error
      throw error
    }

    const responseHasErrors = hasErrors(fetchResult)

    // Emit stale cache warning if we served cached data but server returned errors
    if (hasCachedResult && responseHasErrors) {
      dispatchStaleCacheWarning((fetchResult as {errors: unknown[]}).errors)
    }

    if (preHeated) {
      if (responseHasErrors) {
        const backoffParams = getBackoffParams(fetchResult)
        if (backoffParams.shouldBackoff) {
          circuitBreaker?.reportFailure(
            (backoffParams.errorType as ErrorType) ?? 'Unknown',
            backoffParams.retryAfter,
            backoffParams.resetTime,
          )
        }
      } else if (circuitBreakerStatus === 'OPEN') {
        circuitBreaker?.reportSuccess()
      }
    }

    const clearCache = shouldClearCache<T>(fetchResult, cachedResult)
    const writeToCache = shouldWriteToCache<T>(fetchResult)

    if (storage && clearCache) {
      evictQuery(cacheKey)
    } else if (writeToCache && storage) {
      // Only cache successful results
      if (typeof fetchResult === 'object' && fetchResult !== null && 'data' in fetchResult) {
        queryResultString = JSON.stringify((fetchResult as {[key: string]: unknown}).data)
      }

      const updateType = getUpdateType(!!cachedResult, cachedResultString, queryResultString, preHeated, skipCache)
      tryCacheFetchResult(
        queryId,
        queryName,
        variables,
        fetchResult,
        ttl ?? maxAgeMs,
        {...attributes, update_type: updateType},
        defaultValues,
        preheatSource,
      )
    }

    // For preheating requests, we only want the data in the cache
    // There's no point in populating the store (which is scoped to a session) and it can cause consistency issues
    // See https://github.com/github/issues/issues/19434
    if (preHeated) {
      return
    }
    // fetchResult will be defined if no error was thrown
    return fetchResult
  }

  return {persisterFn, retrieveQuery, tryCacheFetchResult}
}

function removeInternalOnlyAttributes(attributes: {[key: string]: unknown} | undefined) {
  if (!attributes) return undefined

  const cleanedAttributes: {[key: string]: unknown} = {}
  for (const [key, value] of Object.entries(attributes)) {
    if (
      !Object.values(INTERNAL_ONLY_ATTRIBUTES).includes(
        key as (typeof INTERNAL_ONLY_ATTRIBUTES)[keyof typeof INTERNAL_ONLY_ATTRIBUTES],
      )
    ) {
      cleanedAttributes[key] = value
    }
  }
  return cleanedAttributes
}

function getUpdateType(
  hasCachedResult: boolean,
  cachedResult: string | null,
  queryResult: string | null,
  preHeated: boolean | undefined,
  skipCache: boolean | undefined,
) {
  if (skipCache) {
    return 'force_refresh'
  }
  if (!hasCachedResult) {
    return preHeated ? 'pre_heated' : 'new'
  }
  return cachedResult !== queryResult ? 'changed' : 'unchanged'
}

function shouldClearCache<T>(fetchResult: T, cachedResult: T | undefined | Invalid): boolean {
  if (!cachedResult) return false
  if (cachedResult === INVALID) return true
  if (!fetchResult) return false

  return hasErrors(fetchResult)
}

function hasErrors<T>(result: T): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    'errors' in result &&
    Array.isArray(result.errors) &&
    result.errors.length > 0
  )
}

function getBackoffParams<T>(fetchResult: T): {
  shouldBackoff: boolean
  errorType?: string
  retryAfter?: number
  resetTime?: number
} {
  if (!fetchResult) return {shouldBackoff: false}

  if (typeof fetchResult === 'object') {
    if (
      'extensions' in fetchResult &&
      typeof fetchResult.extensions === 'object' &&
      fetchResult.extensions !== null &&
      'backoff' in fetchResult.extensions
    ) {
      const extensions = fetchResult.extensions as {[key: string]: unknown}
      const errorType = typeof extensions.errorType === 'string' ? extensions.errorType : undefined
      const retryAfter = typeof extensions.retryAfter === 'number' ? extensions.retryAfter : undefined
      const resetTime = typeof extensions.resetTime === 'number' ? extensions.resetTime : undefined

      return {shouldBackoff: Boolean(extensions.backoff), errorType, retryAfter, resetTime}
    }
  }
  return {shouldBackoff: false}
}

function dispatchStaleCacheWarning(errors: unknown[]) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent('stale-cache-warning', {
      detail: {
        errors,
      },
    }),
  )
}

/**
 * Dispatches a custom event for staff bar to indicate a cache hit was used for rendering.
 * This is only dispatched when the cached data is actually displayed to the user,
 * not for preheat scenarios. Only dispatched for staff users.
 */
export function dispatchCacheHitEvent() {
  if (typeof window === 'undefined') return
  if (!isStaff()) return

  window.dispatchEvent(new CustomEvent('relay-cache-hit'))
}

function shouldWriteToCache<T>(fetchResult: T): boolean {
  if (!fetchResult) return false

  if (typeof fetchResult === 'object') {
    if ('errors' in fetchResult && Array.isArray(fetchResult.errors) && fetchResult.errors.length > 0) {
      return false
    }
  }
  return true
}
